import React, { useEffect, useState } from 'react';
import { shopifyAPI } from '../services/api';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useTenantUrl } from '../hooks/useTenantUrl';

// Date preset helpers
const getDatePreset = (preset: string): { start: string; end: string } => {
  const now = new Date();
  switch (preset) {
    case 'this_month':
      return {
        start: format(startOfMonth(now), 'yyyy-MM-dd'),
        end: format(endOfMonth(now), 'yyyy-MM-dd'),
      };
    case 'last_month':
      const lastMonth = subMonths(now, 1);
      return {
        start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
        end: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
      };
    case 'this_year':
      return {
        start: format(startOfYear(now), 'yyyy-MM-dd'),
        end: format(endOfYear(now), 'yyyy-MM-dd'),
      };
    case 'last_year':
      const lastYear = subYears(now, 1);
      return {
        start: format(startOfYear(lastYear), 'yyyy-MM-dd'),
        end: format(endOfYear(lastYear), 'yyyy-MM-dd'),
      };
    default:
      return { start: '', end: '' };
  }
};

const Transactions: React.FC = () => {
  const { tenantNavigate } = useTenantUrl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 50;
  const [summary, setSummary] = useState<{
    total_count: number;
    total_payments: number;
    total_refunds: number;
    net_revenue: number;
    payment_methods: Array<{
      name: string;
      payments: number;
      refunds: number;
      net: number;
    }>;
  } | null>(null);
  
  // Initialize filters and page from URL, default to last month if no dates specified
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const defaultDates = getDatePreset('last_month');
  const [filters, setFilters] = useState({
    start_date: searchParams.get('start_date') || defaultDates.start,
    end_date: searchParams.get('end_date') || defaultDates.end,
    transaction_type: searchParams.get('transaction_type') || '',
    payment_method_name: searchParams.get('payment_method_name') || '',
  });
  const [datePreset, setDatePreset] = useState('last_month');

  // Fetch payment methods on mount
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await shopifyAPI.getPaymentMethods();
        setPaymentMethods(response.data.data);
      } catch (err) {
        console.error('Failed to fetch payment methods:', err);
      }
    };
    fetchPaymentMethods();
  }, []);

  // Fetch transactions when page changes
  useEffect(() => {
    updateURLParams(filters, currentPage);
    fetchTransactions();
  }, [currentPage]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      };
      if (filters.start_date) params.start_date = filters.start_date;
      
      // Make end date inclusive by setting to end of day in UTC
      // Database stores timestamps in UTC, so we filter by UTC day
      if (filters.end_date) {
        params.end_date = filters.end_date + 'T23:59:59.999Z';
      }
      
      if (filters.transaction_type) params.transaction_type = filters.transaction_type;
      if (filters.payment_method_name) params.payment_method_name = filters.payment_method_name;

      const response = await shopifyAPI.getTransactions(params);
      setTransactions(response.data.data);
      setSummary(response.data.summary || null);
      
      // Calculate total pages from total count
      const totalCount = response.data.total_count || 0;
      setTotalPages(Math.ceil(totalCount / itemsPerPage));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const updateURLParams = (newFilters: typeof filters, page: number = currentPage) => {
    const params: any = {};
    if (page > 1) params.page = page.toString();
    if (newFilters.start_date) params.start_date = newFilters.start_date;
    if (newFilters.end_date) params.end_date = newFilters.end_date;
    if (newFilters.transaction_type) params.transaction_type = newFilters.transaction_type;
    if (newFilters.payment_method_name) params.payment_method_name = newFilters.payment_method_name;
    
    setSearchParams(params, { replace: true });
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear preset when manually editing dates
    if (name === 'start_date' || name === 'end_date') {
      setDatePreset('custom');
    }
  };

  const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const preset = e.target.value;
    setDatePreset(preset);
    if (preset !== 'custom') {
      const dates = getDatePreset(preset);
      setFilters(prev => ({
        ...prev,
        start_date: dates.start,
        end_date: dates.end,
      }));
    }
  };

  const handleApplyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    updateURLParams(filters, 1);
    fetchTransactions();
  };

  const handleClearFilters = () => {
    const defaultDates = getDatePreset('last_month');
    const clearedFilters = {
      start_date: defaultDates.start,
      end_date: defaultDates.end,
      transaction_type: '',
      payment_method_name: '',
    };
    setFilters(clearedFilters);
    setDatePreset('last_month');
    setCurrentPage(1);
    updateURLParams(clearedFilters, 1);
    fetchTransactions();
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleExport = () => {
    const params: any = {};
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date + 'T23:59:59.999Z';
    if (filters.transaction_type) params.transaction_type = filters.transaction_type;
    if (filters.payment_method_name) params.payment_method_name = filters.payment_method_name;
    shopifyAPI.exportTransactions(params);
  };

  if (loading) {
    return <div className="loading">Loading transactions...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>Transactions</h1>
        <p>View and filter payment and refund transactions</p>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Filters</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ color: '#666', fontSize: '0.9rem' }}>Preset:</span>
            <select
              id="date_preset"
              name="date_preset"
              className="form-control"
              value={datePreset}
              onChange={handlePresetChange}
              style={{ width: 'auto', minWidth: '130px' }}
            >
              <option value="custom">Custom</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_year">This Year</option>
              <option value="last_year">Last Year</option>
            </select>
          </div>
        </div>
        <form onSubmit={handleApplyFilters}>
          <div className="filters">
            <div className="form-group">
              <label htmlFor="start_date">Start Date</label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                className="form-control"
                value={filters.start_date}
                onChange={handleFilterChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="end_date">End Date</label>
              <input
                type="date"
                id="end_date"
                name="end_date"
                className="form-control"
                value={filters.end_date}
                onChange={handleFilterChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="transaction_type">Type</label>
              <select
                id="transaction_type"
                name="transaction_type"
                value={filters.transaction_type}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                <option value="payment">Payment</option>
                <option value="refund">Refund</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="payment_method_name">Payment Method</label>
              <select
                id="payment_method_name"
                name="payment_method_name"
                value={filters.payment_method_name}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                {paymentMethods.map((method) => (
                  <option key={method.name} value={method.name}>
                    {method.name}
                  </option>
                ))}
              </select>
            </div>

          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" className="btn btn-primary">
                Apply Filters
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClearFilters}
              >
                Clear Filters
              </button>
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleExport}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              📥 Export CSV
            </button>
          </div>
        </form>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Accounting Summary */}
      {summary && (
        <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <h2 style={{ color: 'white', marginBottom: '1.5rem' }}>Accounting Summary</h2>
          
          {/* Overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Total Transactions</div>
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>{summary.total_count.toLocaleString()}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>💰 Cash Received</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#9ae6b4' }}>${summary.total_payments.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div style={{ fontSize: '0.7rem', opacity: 0.7, marginTop: '0.25rem' }}>Payments processed</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>↩️ Cash Returned</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#fc8181' }}>${summary.total_refunds.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div style={{ fontSize: '0.7rem', opacity: 0.7, marginTop: '0.25rem' }}>Refunds processed</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>✅ Net Revenue</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#68d391' }}>${summary.net_revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div style={{ fontSize: '0.7rem', opacity: 0.7, marginTop: '0.25rem' }}>What you keep</div>
            </div>
          </div>
          
          {/* Payment Method Breakdown */}
          {summary.payment_methods && summary.payment_methods.length > 0 && (
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '1.5rem' }}>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '1rem' }}>Cash Flow by Payment Method</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                {summary.payment_methods.map((pm) => (
                  <div key={pm.name} style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.75rem' }}>
                    <div style={{ fontSize: '0.75rem', opacity: 0.8, marginBottom: '0.5rem', fontWeight: 600 }}>
                      {pm.name || 'Unknown'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.7rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ opacity: 0.7 }}>Received:</span>
                        <span style={{ fontWeight: 600, color: '#9ae6b4' }}>${pm.payments.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ opacity: 0.7 }}>Returned:</span>
                        <span style={{ fontWeight: 600, color: '#fc8181' }}>${pm.refunds.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.25rem', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                        <span style={{ opacity: 0.9, fontWeight: 600 }}>Net:</span>
                        <span style={{ fontWeight: 700, color: '#68d391', fontSize: '0.85rem' }}>${pm.net.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Formula */}
          <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.2)', fontSize: '0.75rem', opacity: 0.8, textAlign: 'center' }}>
            <strong>Net Revenue</strong> (${summary.net_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}) = <strong>Cash Received</strong> (${summary.total_payments.toLocaleString(undefined, { minimumFractionDigits: 2 })}) − <strong>Cash Returned</strong> (${summary.total_refunds.toLocaleString(undefined, { minimumFractionDigits: 2 })})
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>Transactions</h2>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <span style={{ color: '#718096', fontSize: '0.9rem' }}>
              Page {currentPage} of {totalPages}
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn btn-secondary"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                style={{ padding: '0.5rem 1rem' }}
              >
                Previous
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
                style={{ padding: '0.5rem 1rem' }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
        
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Shopify TX ID</th>
                <th>Order #</th>
                <th>Date & Time</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Payment Method</th>
                <th>Status</th>
                <th>Expected Payout</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                    No transactions found
                  </td>
                </tr>
              ) : (
                transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#718096' }}>
                        {transaction.shopify_transaction_id || 'N/A'}
                      </span>
                    </td>
                    <td>
                      {transaction.order_id ? (
                        <button
                          onClick={() => tenantNavigate(`/orders/${transaction.order_id}`)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#4299e1',
                            cursor: 'pointer',
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            padding: 0
                          }}
                        >
                          {transaction.order_name || `#${transaction.order_number}`}
                        </button>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td>{format(new Date(transaction.transaction_date), 'MMM dd, yyyy HH:mm')}</td>
                    <td>
                      <span className={`status-badge ${
                        transaction.transaction_type === 'payment' ? 'status-reconciled' : 'status-pending'
                      }`}>
                        {transaction.transaction_type}
                      </span>
                    </td>
                    <td>${Number(transaction.amount).toFixed(2)}</td>
                    <td>{transaction.payment_method_name || 'N/A'}</td>
                    <td>{transaction.status}</td>
                    <td>
                      {transaction.expected_payout_date
                        ? format(new Date(transaction.expected_payout_date), 'MMM dd, yyyy')
                        : 'N/A'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Transactions;

